import React from 'react';
import { useTranslation } from 'react-i18next';

import FAQContent from '../components/Content/FAQContent';
import FaqDocumentView from '../components/Content/FaqDocumentView';
import PolicyDocumentToc from '../components/PolicyContent/PolicyDocumentToc';
import { buildFaqPageJsonLd, buildFaqToc, faqDocument } from '../content/faq';
import MarketingPageFooter from '../components/marketing/MarketingPageFooter';
import MarketingLandingNav, { MARKETING_NAV_TOP_OFFSET } from '../components/marketing/MarketingLandingNav';
import { SEO } from '../shared/components/SEO';
import { buildBreadcrumbJsonLd } from '../shared/seo/breadcrumbJsonLd';
import { isEnglishLocale } from '../i18n/localeUtils';
import { getLocalizedFaqSeo } from '../content/i18n/marketingPageSeo';

interface FAQPageProps {
  onNavigate?: (view: string) => void;
}

const FAQPage: React.FC<FAQPageProps> = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  const useLocalizedBody = !isEnglishLocale(i18n.language);
  const seo = getLocalizedFaqSeo(t);
  const toc = buildFaqToc(faqDocument.categories);

  return (
    <div className="policy-doc-page policy-doc-page--faq min-h-screen flex flex-col">
      <SEO
        title={useLocalizedBody ? t('views.faq.title') : seo.title}
        description={useLocalizedBody ? t('views.faq.subtitle') : seo.description}
        canonical="https://www.gograbjob.com/faqs"
        jsonLd={
          useLocalizedBody
            ? [
                buildBreadcrumbJsonLd([
                  { name: t('common.home', { defaultValue: 'Home' }), path: '/' },
                  { name: t('views.faq.title') },
                ]),
              ]
            : [
                buildBreadcrumbJsonLd([
                  { name: t('common.home', { defaultValue: 'Home' }), path: '/' },
                  { name: seo.breadcrumb },
                ]),
                buildFaqPageJsonLd(faqDocument),
              ]
        }
      />

      <MarketingLandingNav onNavigate={onNavigate} variant="marketing" />

      <main className={`policy-doc-page__main ${MARKETING_NAV_TOP_OFFSET}`} key={i18n.language}>
        {useLocalizedBody ? (
          <div className="policy-doc-layout policy-doc-layout--localized">
            <div className="policy-doc-layout__content policy-doc-layout__content--full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <FAQContent onNavigate={onNavigate} />
            </div>
          </div>
        ) : (
          <div className="policy-doc-layout">
            <PolicyDocumentToc entries={toc} />
            <div className="policy-doc-layout__content">
              <FaqDocumentView document={faqDocument} onNavigate={onNavigate} />
            </div>
          </div>
        )}
      </main>

      <MarketingPageFooter onNavigate={onNavigate} theme="faq" />
    </div>
  );
};

export default FAQPage;
